<template>
    <div>
        <div class="flex flex-col gap-y-0.5 text-gray-370 dark:text-gray-110 mt-2">
            <template
                v-for="route in topMenu"
                :key="route.id"
            >
                <CustomRouterLink
                    class="h-9 flex items-center gap-x-3 cursor-pointer transition-colors"
                    :class="[mini ? 'justify-center mx-auto w-9 !h-10 rounded-1.25' : 'mx-2 px-2 rounded-1.25', menuStyle(route)]"
                    :route="route"
                    :mini="mini"
                    :simpleRote="!!route.routeName"
                    @click="navigationActionHandler(route)"
                >
                    <div class="w-5 flex item-center justify-center dark:text-gray-200">
                        <component :is="route.icon()" />
                    </div>

                    <div
                        v-if="!mini"
                        class="text-center text-xs whitespace-nowrap"
                    >
                        {{ route.name }}
                    </div>
                </CustomRouterLink>
            </template>
        </div>

        <div
            class="my-2 border-t border-gray-100 dark:border-gray-730"
            :class="mini ? 'mx-2' : 'mx-4'"
        ></div>

        <RoutingSideBar
            :mini="mini"
            :showMobileSideBars="showMobileSideBars"
        />
    </div>

    <GlobalSearchDialog v-model="showSearchDialog" />
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import GlobalSearchDialog from '@/components/dialogs/GlobalSearchDialog.vue'
import CustomRouterLink from '@/components/navigation/CustomRouterLink.vue'
import RoutingSideBar from '@/components/navigation/RoutingSideBar/RoutingSideBar.vue'

import { useAuthorizationStore } from '@/store/authorizationStore'
import { useWhiteLabelStore } from '@/store/call-tracking/whiteLabelStore'
import { useTwoFactorAuthentication } from '@/store/call-tracking/twoFactorAuthentication'

import {
    SEARCH, GETTING_START, TOP_MENU_ACCOUNT_FOR_OWNER, TOP_MENU_ACCOUNT_FOR_MANAGER,
} from '@/constants/accountMenu'
import { NAVIGATION_ACTIONS } from '@/constants/navigationActions'

defineProps({
    mini: {
        type: Boolean,
        default: false,
    },
    showMobileSideBars: {
        type: Boolean,
        default: false,
    },
})

const authorizationStore = useAuthorizationStore()
const whiteLabelStore = useWhiteLabelStore()
const twoFactorAuthentication = useTwoFactorAuthentication()

const route = useRoute()

const showSearchDialog = ref(false)

const isVendor = computed(() => authorizationStore.isVendor)
const isBuyer = computed(() => authorizationStore.isBuyer)
const isReporter = computed(() => authorizationStore.isReporter)
const isOwner = computed(() => authorizationStore.isOwner)

const menuOwner = computed(() => (!whiteLabelStore.whiteLabelIsEnabled
    ? [...TOP_MENU_ACCOUNT_FOR_OWNER, GETTING_START]
    : TOP_MENU_ACCOUNT_FOR_OWNER))

const topMenu = computed(() => {
    if (authorizationStore.isWorkspaceOwner) {
        return menuOwner.value
    }

    if (!isReporter.value && !isVendor.value && !isBuyer.value) {
        return TOP_MENU_ACCOUNT_FOR_MANAGER
    }

    return [SEARCH]
})

function navigationActionHandler(route) {
    if (!route.hasOwnProperty('action')) {
        return
    }

    switch (route.action) {
    case NAVIGATION_ACTIONS.search:
        openSearchDialog()
        break
    }
}

function checkCurrentLink(parent, subMenu) {
    const { routeName, isFirstLoad } = parent

    if (subMenu) {
        const isActive = subMenu.some((el) => el.routeName === route.meta.reference)

        if (isActive && isFirstLoad) {
            parent.isOpenSub = isActive
            parent.isFirstLoad = false
        }

        return isActive
    }
    return route.meta.reference === routeName
}

function menuStyle(route) {
    const { subMenu } = route

    const active = checkCurrentLink(route, subMenu)

    return active
        ? 'bg-purple-50 dark:bg-menu-blue770 text-purple dark:text-purple-140'
        : 'hover:bg-menu-purple70 hover:text-gray-370 dark:hover:text-white dark:hover:bg-menu-blue770'
}

function openSearchDialog() {
    if (authorizationStore.isAccountReview) {
        return
    }

    if (isOwner.value && authorizationStore.isRequiredUserTwoFactor) {
        twoFactorAuthentication.setConfirmDialogVisible(true)
        return
    }

    showSearchDialog.value = !showSearchDialog.value
}
</script>
